<template>
  <Teleport v-if="mounted && single" to="#header-breadcrumbs">
    <CBreadcrumb v-bind="{ routes }" />
  </Teleport>
  <div class="single-wrapper">
    <CBotCoins
      v-if="!single?.is_deleted"
      :table-data="tableData"
      :loading="loading"
      :pagination-data="paginationData"
      :detail-info="singleDetailInfo"
      @sellCoin="sellCoin"
      @onPageChange="onPageChange"
      @onChangeLimit="onChangeLimit"
    />
    <CBotHistory :is-deleted="single?.is_deleted" />
  </div>
  <CModalCoinSell
    :show="showSellModal"
    :pair="coinPair"
    :loading="sellLoading"
    @close="showSellModal = false"
    @submit="sellCurrentCoin"
  />
</template>

<script setup lang="ts">
import { useMounted } from "@/composables/useMounted";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import ApiService from "@/services/ApiService";
import { useRoute } from "vue-router";
import CBotHistory from "@/modules/Bots/components/CBotHistory.vue";
import CBotCoins from "@/modules/Bots/components/CBotCoins.vue";
import { useTableFetch } from "@/composables/useTableFetch";
import { formatMoneyDecimal, handleError } from "@/utils";
import CModalCoinSell from "@/modules/Bots/components/CModalCoinSell.vue";
import { useI18n } from "vue-i18n";

import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();

const { mounted } = useMounted();
const route = useRoute();
const { t } = useI18n();

const single = ref();

const coinID = ref(0);
const coinPair = ref("");
const showSellModal = ref(false);
const sellLoading = ref(false);

const historyTable = ref(null);

const routes = computed(() => [
  {
    name: single.value?.strategy?.base_strategy_name,
    route: "/bots",
  },
]);

const {
  tableData,
  paginationData,
  onPageChange,
  onChangeLimit,
  loading,
  fetchTableData,
} = useTableFetch(`/bot/${route.params.botId}/trades/?is_open=true`);

const singleDetailInfo = computed(() => [
  {
    value: single.value?.strategy?.base_strategy_name,
    title: t("strategy"),
  },
  {
    value:
      formatMoneyDecimal(single.value?.exchange?.usdt_balance ?? 0, 2) +
      " USDT",
    title: t("bot_coin_balance"),
  },
  {
    value: formatMoneyDecimal(single.value?.assigned_money ?? 0, 2) + " USDT",
    title: t("coin_assigned", { count: "USDT" }),
  },
  {
    value:
      formatMoneyDecimal(single.value?.profit_today ?? 0, 2) +
      " " +
      single.value?.currency,
    title: t("today"),
  },
  {
    value:
      formatMoneyDecimal(single.value?.profit_week ?? 0, 2) +
      " " +
      single.value?.currency,
    title: t("weekly_7"),
  },
  {
    value:
      formatMoneyDecimal(single.value?.profit_month ?? 0, 2) +
      " " +
      single.value?.currency,
    title: t("monthly_30"),
  },
  {
    value:
      formatMoneyDecimal(single.value?.profit_all_coin ?? 0, 2) +
      " " +
      single.value?.currency,
    title: t("total"),
  },
]);

const sellCoin = (coin: number, pair: string) => {
  coinID.value = coin;
  coinPair.value = pair;
  showSellModal.value = true;
};
const sellCurrentCoin = async () => {
  sellLoading.value = true;
  ApiService.post(`/bot/${route.params.botId}/trades/${coinID.value}/sell/`)
    .then(() => {
      fetchTableData();
      historyTable.value?.fetchTableData();
      showSellModal.value = false;
    })
    .catch((err) => {
      handleError(err?.response?.data);
    })
    .finally(() => {
      sellLoading.value = false;
    });
};

function getSingle() {
  ApiService.get(`/bot/${route.params.botId}/`).then((res) => {
    single.value = res?.data;
  });
}

onMounted(() => {
  getSingle();
  pushToDataLayer("pageview", {
    page: "bot_single",
    bot_id: route.params.botId,
    bot_name: single.value?.strategy?.base_strategy_name,
    event_category: "page",
    event_label: "bot_single_page_view",
    button_text: "None",
  });
});
</script>

<style scoped>
@media screen and (max-width: 1207px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 960px !important;
    overflow-x: auto !important;
  }
}
@media screen and (max-width: 1115px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 868px !important;
    overflow-x: auto !important;
  }
}
@media screen and (max-width: 1024px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 800px !important;
    overflow-x: auto !important;
  }
}
@media screen and (max-width: 955px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 768px !important;
    overflow-x: auto !important;
  }
}
@media screen and (max-width: 922px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 720px !important;
    overflow-x: auto !important;
  }
}
@media screen and (max-width: 870px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 640px !important;
    overflow-x: auto !important;
  }
}
@media screen and (max-width: 795px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 620px !important;
    overflow-x: auto !important;
  }
}
@media screen and (max-width: 768px) {
  .single-wrapper {
    margin: 0 auto;
    max-width: 100% !important;
    overflow-x: auto !important;
  }
}
</style>
